import React, { useEffect, useState } from 'react';
import {
    nextPage,
    prevPage,
    goToPage,
    handlePageChange,
    getCurrentPage,
} from '../../utils/readiumUtils';
import { ArrowRightBlack, ExitFullScreen, FullScreenIcon } from '../../assets';
import { RootState } from '../../store';
import { useDispatch, useSelector } from 'react-redux';

import { Tooltip } from 'antd';
import ViewMenu from '../view-menu/ViewMenu';
import Actions from '../actions-menu/Actions';
import { trackEvent } from '../../segment';
import { setFullScreenModeEnabled } from '../../features/FullScreenMode/fullScreenMode';
import { Root } from 'react-dom/client';
import { setActiveTab } from '../../features/toggle/toggleSlice';
import { setWidgetOpen } from '../../features/Widget/WidgetOpenSlice';
import WidgetsIcon from '../../assets/images/WidgetsIcon.svg';

interface PageProps {
    offset: number;
    isPopupOpen: boolean;
    close: () => void;
    bookUri: string;
    bookId: string;
    currPage?: string;
    widgetsData: any;
}

const PageNavigatorComponent: React.FC<PageProps> = ({
    offset,
    isPopupOpen,
    close,
    bookUri,
    bookId,
    currPage,
    widgetsData
}) => {
    const [pageInput, setPageInput] = useState<number | string>('');
    const isFullScreen = useSelector(
        (state: RootState) => state.FullScreenMode.isEnabled
    );
    const book_title = useSelector(
        (state: RootState) => state.currentBook.name
    );
    const dispatch = useDispatch();
    const isTOCOpen = useSelector((state: RootState) => state.TOCOpen.isOpen);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
        // eslint-disable-next-line
    }, [isPopupOpen]);

    const handleKeyDown = (event: any) => {
        if (isFullScreen) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        if (event.key === 'Escape') {
            close();
        }
        if (isPopupOpen) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        const eventKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        if (eventKeys.includes(event.key)) {
            if (event.key === 'ArrowLeft') {
                prevPage(window);
            } else if (event.key === 'ArrowRight') {
                nextPage(window);
            } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.preventDefault();
            }
            handlePageChange(window);
        }
    };

    const pageChangeLeft = () => {
        //old event
        trackEvent('epub_previous_clicked', {
            book_title: book_title,
        });
        prevPage(window);
        handlePageChange(window, 'click_previous_page');
    };

    const pageChangeRight = () => {
        //old event
        trackEvent('epub_next_clicked', {
            book_title: book_title,
        });
        nextPage(window);
        handlePageChange(window, 'click_next_page');
    };

    const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPageInput(value ? parseInt(value, 10) : '');
    };

    const handleGoToPage = () => {
        //old event
        trackEvent('epub_goto_clicked', {
            book_title: book_title,
        });
        if (typeof pageInput === 'number') {
            const currentPageNumber = +getCurrentPage(window)?.slice(4) || 0;
            // openPageIndex is 0-based, but page labels (and offset) are 1-based, so -1.
            const goTopageNumber = pageInput + offset - 1;
            goToPage(window, goTopageNumber);
            handlePageChange(
                window,
                'click_goto_page',
                currentPageNumber.toString()
            );
            setPageInput('');
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const charCode = e.charCode || e.keyCode;
        if (charCode < 48 || charCode > 57) {
            e.preventDefault();
        }
        if (e.key === 'Enter') {
            handleGoToPage();
        }
    };
    const enterFullScreen = () => {
        //old event
        trackEvent('full_screen_clicked', {});
        trackEvent('click_fullscreen_book', {});
        const doc = document.documentElement;
        doc.requestFullscreen();
        dispatch(setFullScreenModeEnabled(true));
    };

    const exitFullScreen = () => {
        if (document.exitFullscreen) {
            trackEvent('click_exit_fullscreen_book', {});
            document.exitFullscreen();
            dispatch(setFullScreenModeEnabled(false));
        }
    };
    const WidgetclickHandler = () => {
        trackEvent('click_widget', {});
        dispatch(setActiveTab('openBook'));
        dispatch(setWidgetOpen(true));
    };

    return (
        <div className="flex flex-col items-center justify-center h-full">
            <Tooltip
                title={isFullScreen ? 'Exit Fullscreen' : 'Fullscreen'}
                placement="top"
            >
                <div className="flex flex-col">
                    <button
                        data-testid="fullscreen-button"
                        onClick={
                            isFullScreen ? exitFullScreen : enterFullScreen
                        }
                        className="flex items-center justify-center w-[50px] min-w-[40px] bg-white px-2 py-2 rounded-lg"
                    >
                        <img
                            src={isFullScreen ? ExitFullScreen : FullScreenIcon}
                            alt="fullscreen"
                        />
                    </button>
                </div>
            </Tooltip>
            <div className="flex flex-col py-[1.938rem]">
                <div
                    className=" flex flex-col items-center justify-center bg-white border rounded-bl-md w-[60px] h-[50px] md:w-[50px] md:h-[50px]"
                    style={{
                        borderRadius: '6px 6px 0px 0px',
                        borderWidth: '0px 0px 1px 0px',
                        borderColor: '#E3E3E3',
                    }}
                >
                    <input
                        type="number"
                        value={pageInput}
                        onChange={handlePageInputChange}
                        onKeyPress={handleKeyPress}
                        className="w-[40px] h-[25px] md:w-[35px] md:h-[25px] text-center border rounded-t-md border-gray-400"
                        style={{
                            fontSize: '12px',
                            boxSizing: 'border-box',
                            MozAppearance: 'textfield',
                        }}
                        min={1}
                    />
                    <button
                        data-testid="goto-button"
                        onClick={handleGoToPage}
                        className="bg-white border border-l-0 rounded-t-md w-[60px] h-[16px] md:w-[35px] md:h-[16px] flex items-center justify-center"
                        style={{
                            borderRadius: '6px 6px 0px 0px',
                            borderWidth: '0px 0px 0px 0px',
                            borderColor: '#E3E3E3',
                            boxSizing: 'border-box',
                        }}
                    >
                        <svg
                            width="35"
                            height="16"
                            viewBox="0 0 35 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M0 11C0 13.7614 2.23858 16 5 16H30C32.7614 16 35 13.7614 35 11V0H0V11Z"
                                fill="#7B86A0"
                            />
                            <path
                                d="M13.91 3.93C15.47 3.93 16.71 4.72 17.16 6.12H15.55C15.23 5.53 14.65 5.22 13.91 5.22C12.66 5.22 11.79 6.11 11.79 7.5C11.79 8.92 12.67 9.8 13.97 9.8C15.05 9.8 15.74 9.18 15.95 8.19H13.55V7.12H17.33V8.34C17.05 9.76 15.78 11.06 13.92 11.06C11.89 11.06 10.35 9.59 10.35 7.5C10.35 5.41 11.89 3.93 13.91 3.93ZM25.1758 7.49C25.1758 9.59 23.5858 11.07 21.6058 11.07C19.6358 11.07 18.0258 9.59 18.0258 7.49C18.0258 5.4 19.6358 3.92 21.6058 3.92C23.5958 3.92 25.1758 5.4 25.1758 7.49ZM19.4658 7.49C19.4658 8.91 20.3358 9.82 21.6058 9.82C22.8658 9.82 23.7358 8.91 23.7358 7.49C23.7358 6.07 22.8658 5.18 21.6058 5.18C20.3358 5.18 19.4658 6.07 19.4658 7.49Z"
                                fill="white"
                            />
                        </svg>
                    </button>
                </div>
                <Tooltip title={'Next Page'} placement="left">
                    <button
                        data-testid="page-navigator-right-button"
                        onClick={pageChangeRight}
                        className="py-6 flex items-center justify-center bg-white border rounded-bl-md w-[60px] h-[30px] md:w-[50px] md:h-[30px]"
                        style={{
                            borderRadius: '0px 0px 0px 0px',
                            borderWidth: '0px 0px 1px 0px',
                            borderColor: '#E3E3E3',
                        }}
                    >
                        <img
                            src={ArrowRightBlack}
                            alt="arrow"
                            className="h-4"
                        />
                    </button>
                </Tooltip>
                <Tooltip title={'Previous page'} placement="left">
                    <button
                        data-testid="page-navigator-left-button"
                        onClick={pageChangeLeft}
                        className="py-6 flex items-center justify-center bg-white border rounded-tl-md w-[60px] h-[30px] md:w-[50px] md:h-[30px]"
                        style={{
                            borderRadius: '0px 0px 6px 6px',
                            borderWidth: '0px 0px 0 0px',
                            borderColor: '#E3E3E3',
                        }}
                    >
                        <img
                            src={ArrowRightBlack}
                            alt="arrow"
                            className="h-4 rotate-180"
                        />
                    </button>
                </Tooltip>
            </div>
            <div className="flex flex-col items-center justify-center space-y-2">
                {
                    widgetsData &&
                    (
                        <div
                            className={`flex items-center justify-center w-[50px] min-w-[40px] h-[50px] min-h-[40px] bg-white px-2 py-2 rounded-lg
                            ${isTOCOpen ? 'z-0' : ''}`}
                        >

                            <button
                                onClick={WidgetclickHandler}
                            >
                                <img
                                    src={WidgetsIcon}
                                    alt="WidgetsIcon"
                                    className='ml-[10px]'
                                />
                                <p className="text-[#7B86A0] font-semibold text-xs">Widget</p>
                            </button>
                        </div>
                    )}
                <div
                    className={`flex items-center justify-center w-[50px] min-w-[40px] h-[50px] min-h-[40px] bg-white px-2 py-2 rounded-lg
                    ${isTOCOpen ? 'z-0' : ''}
                    `}
                >
                    <ViewMenu />
                </div>
                <div
                    className={`flex items-center justify-center w-[50px] min-w-[40px] h-[50px] min-h-[40px] bg-white px-2 py-2 rounded-lg
                    ${isTOCOpen ? 'z-0' : ''}
                    `}
                >
                    <Actions
                        bookUri={bookUri}
                        offset={offset}
                        bookId={bookId}
                    />
                </div>
            </div>
        </div>
    );
};

export default PageNavigatorComponent;
